import React, { Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.min.js';
import 'leaflet/dist/leaflet.css';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import i18n from './i18n/config';
import { loadFromLocalStorage } from '@utils/storage';
import '@styles/GlobalStyles.css';
import { SiteSetup } from '@pages/SiteSetup';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { useDynamicManifest } from '@hooks/useDynamicManifest';
import { InstallPWAPrompt } from '@components/atoms/InstallPWAPrompt/InstallPWAPrompt';
import { useGetAllLanguages } from '@services/useGetAllLanguages';
import { DataProvider, NotificationsProvider, ServicesCartProvider, ThemesProvider } from '@context';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Lazy loaded pages - marketplace
const HomePage = React.lazy(() => import('@pages/marketplace/homePage').then(m => ({ default: m.HomePage })));
const ProductsPage = React.lazy(() => import('@pages/marketplace/productsPage').then(m => ({ default: m.ProductsPage })));
const ProductsSearchResultsPage = React.lazy(() => import('@pages/marketplace/productsSearchResultsPage').then(m => ({ default: m.ProductsSearchResultsPage })));
const ProductsSubcategoryPage = React.lazy(() => import('@pages/marketplace/productsSubcategoryPage').then(m => ({ default: m.ProductsSubcategoryPage })));
const ProductDetailPage = React.lazy(() => import('@pages/marketplace/productDetailPage').then(m => ({ default: m.ProductDetailPage })));
const ServicesPage = React.lazy(() => import('@pages/marketplace/servicesPage').then(m => ({ default: m.ServicesPage })));
const ServicesSearchResultsPage = React.lazy(() => import('@pages/marketplace/servicesSearchResultsPage').then(m => ({ default: m.ServicesSearchResultsPage })));
const MerchantsPage = React.lazy(() => import('@pages/marketplace/merchantsPage').then(m => ({ default: m.MerchantsPage })));
const MerchantDetailPage = React.lazy(() => import('@pages/marketplace/merchantDetailPage').then(m => ({ default: m.MerchantDetailPage })));
const BasketsPage = React.lazy(() => import('@pages/marketplace/basketsPage').then(m => ({ default: m.BasketsPage })));
const BasketDetailPage = React.lazy(() => import('@pages/marketplace/basketDetailPage').then(m => ({ default: m.BasketDetailPage })));
const CampaignsPage = React.lazy(() => import('@pages/marketplace/campaignsPage').then(m => ({ default: m.CampaignsPage })));
const ContactsPage = React.lazy(() => import('@pages/marketplace/contactsPage').then(m => ({ default: m.ContactsPage })));
const HowToUsePointsPage = React.lazy(() => import('@pages/marketplace/howToUsePointsPage').then(m => ({ default: m.HowToUsePointsPage })));
const PointsServicesPage = React.lazy(() => import('@pages/marketplace/pointsServicesPage').then(m => ({ default: m.PointsServicesPage })));
const PointsServiceDetailPage = React.lazy(() => import('@pages/marketplace/pointsServiceDetailPage').then(m => ({ default: m.PointsServiceDetailPage })));
const PolicyPage = React.lazy(() => import('@pages/marketplace/policyPage').then(m => ({ default: m.PolicyPage })));

// Lazy loaded pages - authentication
const CreateAccountPage = React.lazy(() => import('@pages/authentication/createAccountPage').then(m => ({ default: m.CreateAccountPage })));
const LoginPage = React.lazy(() => import('@pages/authentication/loginPage').then(m => ({ default: m.LoginPage })));
const PasswordRecoveryPage = React.lazy(() => import('@pages/authentication/passwordRecoveryPage').then(m => ({ default: m.PasswordRecoveryPage })));
const SetNewPasswordPage = React.lazy(() => import('@pages/authentication/setNewPasswordPage').then(m => ({ default: m.SetNewPasswordPage })));
const TwoFactorAuthActivatePage = React.lazy(() => import('@pages/authentication/twoFactorAuthActivatePage').then(m => ({ default: m.TwoFactorAuthActivatePage })));
const TwoFactorAuthVerifyPage = React.lazy(() => import('@pages/authentication/twoFactorAuthVerifyPage').then(m => ({ default: m.TwoFactorAuthVerifyPage })));

// Lazy loaded pages - shopping cart
const ShoppingCartPage = React.lazy(() => import('@pages/shoppingCart/shoppingCartPage').then(m => ({ default: m.ShoppingCartPage })));
const BillingPage = React.lazy(() => import('@pages/shoppingCart/billingPage').then(m => ({ default: m.BillingPage })));
const DeliveryAddressPage = React.lazy(() => import('@pages/shoppingCart/deliveryAddressPage').then(m => ({ default: m.DeliveryAddressPage })));
const PaymentPage = React.lazy(() => import('@pages/shoppingCart/paymentPage').then(m => ({ default: m.PaymentPage })));
const ConfirmOrderPage = React.lazy(() => import('@pages/shoppingCart/confirmOrderPage').then(m => ({ default: m.ConfirmOrderPage })));
const FeedbackPage = React.lazy(() => import('@pages/shoppingCart/feedbackPage').then(m => ({ default: m.FeedbackPage })));
const CardPaymentRedirectPage = React.lazy(() => import('@pages/shoppingCart/cardPaymentRedirectPage').then(m => ({ default: m.CardPaymentRedirectPage })));

// Lazy loaded pages - services cart
const ServicesCartPage = React.lazy(() => import('@pages/servicesCart/servicesCartPage').then(m => ({ default: m.ServicesCartPage })));
const ServicesBookingEmployeesPage = React.lazy(() => import('@pages/servicesCart/servicesBookingEmployeesPage').then(m => ({ default: m.ServicesBookingEmployeesPage })));
const ServicesBookingSchedulingPage = React.lazy(() => import('@pages/servicesCart/servicesBookingSchedulingPage').then(m => ({ default: m.ServicesBookingSchedulingPage })));
const ServicesBookingPaymentPage = React.lazy(() => import('@pages/servicesCart/servicesBookingPaymentPage').then(m => ({ default: m.ServicesBookingPaymentPage })));
const ServicesBookingConfirmPage = React.lazy(() => import('@pages/servicesCart/servicesBookingConfirmPage').then(m => ({ default: m.ServicesBookingConfirmPage })));
const ServicesBookingFeedbackPage = React.lazy(() => import('@pages/servicesCart/servicesBookingFeedbackPage').then(m => ({ default: m.ServicesBookingFeedbackPage })));

// Lazy loaded pages - my account
const DashboardPage = React.lazy(() => import('@pages/myAccount/dashboardPage').then(m => ({ default: m.DashboardPage })));
const ComplaintPage = React.lazy(() => import('@pages/myAccount/complaintPage').then(m => ({ default: m.ComplaintPage })));
const MyProfilePage = React.lazy(() => import('@pages/myAccount/myProfilePage').then(m => ({ default: m.MyProfilePage })));
const MyOrdersPage = React.lazy(() => import('@pages/myAccount/myOrdersPage').then(m => ({ default: m.MyOrdersPage })));
const MyOrderDetailPage = React.lazy(() => import('@pages/myAccount/myOrderDetailPage').then(m => ({ default: m.MyOrderDetailPage })));
const MyProductReservationsPage = React.lazy(() => import('@pages/myAccount/myProductReservationsPage').then(m => ({ default: m.MyProductReservationsPage })));
const MyProductReservationDetailPage = React.lazy(() => import('@pages/myAccount/myProductReservationDetailPage').then(m => ({ default: m.MyProductReservationDetailPage })));
const MyCompaniesPage = React.lazy(() => import('@pages/myAccount/myCompaniesPage').then(m => ({ default: m.MyCompaniesPage })));
const MyCompanyCreatePage = React.lazy(() => import('@pages/myAccount/myCompanyCreatePage').then(m => ({ default: m.MyCompanyCreatePage })));
const MyCompanyPage = React.lazy(() => import('@pages/myAccount/myCompanyPage').then(m => ({ default: m.MyCompanyPage })));
const MyAdherencesPage = React.lazy(() => import('@pages/myAccount/myAdherencesPage').then(m => ({ default: m.MyAdherencesPage })));
const MyAdherenceDetailPage = React.lazy(() => import('@pages/myAccount/myAdherenceDetailPage').then(m => ({ default: m.MyAdherenceDetailPage })));
const MyCouponsPage = React.lazy(() => import('@pages/myAccount/myCouponsPage').then(m => ({ default: m.MyCouponsPage })));
const MyCouponsCreatePage = React.lazy(() => import('@pages/myAccount/myCouponsCreatePage').then(m => ({ default: m.MyCouponsCreatePage })));
const MyCouponsEditPage = React.lazy(() => import('@pages/myAccount/myCouponsEditPage').then(m => ({ default: m.MyCouponsEditPage })));
const ManageServicesPage = React.lazy(() => import('@pages/myAccount/manageServicesPage').then(m => ({ default: m.ManageServicesPage })));
const ManageServicesEditPage = React.lazy(() => import('@pages/myAccount/manageServicesEditPage').then(m => ({ default: m.ManageServicesEditPage })));
const ManageServicesCreatePage = React.lazy(() => import('@pages/myAccount/manageServicesCreatePage').then(m => ({ default: m.ManageServicesCreatePage })));
const ManageWorkersPage = React.lazy(() => import('@pages/myAccount/manageWorkersPage').then(m => ({ default: m.ManageWorkersPage })));
const ManageWorkersEditPage = React.lazy(() => import('@pages/myAccount/manageWorkersEditPage').then(m => ({ default: m.ManageWorkersEditPage })));
const ManageWorkersCreatePage = React.lazy(() => import('@pages/myAccount/manageWorkersCreatePage').then(m => ({ default: m.ManageWorkersCreatePage })));
const ManageSchedulePage = React.lazy(() => import('@pages/myAccount/manageSchedulePage').then(m => ({ default: m.ManageSchedulePage })));
const ManageProductsPage = React.lazy(() => import('@pages/myAccount/manageProductsPage').then(m => ({ default: m.ManageProductsPage })));
const ManageProductsCreatePage = React.lazy(() => import('@pages/myAccount/manageProductsCreatePage').then(m => ({ default: m.ManageProductsCreatePage })));
const ManageProductsEditPage = React.lazy(() => import('@pages/myAccount/manageProductsEditPage').then(m => ({ default: m.ManageProductsEditPage })));
const ManageOrdersPage = React.lazy(() => import('@pages/myAccount/manageOrdersPage').then(m => ({ default: m.ManageOrdersPage })));
const ManageOrderDetailPage = React.lazy(() => import('@pages/myAccount/manageOrderDetailPage').then(m => ({ default: m.ManageOrderDetailPage })));
const ManageProductReservationsPage = React.lazy(() => import('@pages/myAccount/manageProductReservationsPage').then(m => ({ default: m.ManageProductReservationsPage })));
const ManageProductReservationDetailPage = React.lazy(() => import('@pages/myAccount/manageProductReservationDetailPage').then(m => ({ default: m.ManageProductReservationDetailPage })));
const MyAccountPointsHistoryPage = React.lazy(() => import('@pages/myAccount/myAccountPointsHistoryPage').then(m => ({ default: m.MyAccountPointsHistoryPage })));
const ManageAddressesPage = React.lazy(() => import('@pages/myAccount/manageAddressesPage').then(m => ({ default: m.ManageAddressesPage })));
const ManageAddressesEditPage = React.lazy(() => import('@pages/myAccount/manageAddressesEditPage').then(m => ({ default: m.ManageAddressesEditPage })));
const ManageAddressesCreatePage = React.lazy(() => import('@pages/myAccount/manageAddressesCreatePage').then(m => ({ default: m.ManageAddressesCreatePage })));
const DeleteAccountPage = React.lazy(() => import('@pages/myAccount/deleteAccountPage').then(m => ({ default: m.DeleteAccountPage })));
const HelpCentrePage = React.lazy(() => import('@pages/myAccount/helpCentrePage').then(m => ({ default: m.HelpCentrePage })));
const DeleteAccountCompletedPage = React.lazy(() => import('@pages/myAccount/deleteAccountCompletedPage/DeleteAccountCompletedPage').then(m => ({ default: m.DeleteAccountCompletedPage })));
const MyAccountLegalPage = React.lazy(() => import('@pages/myAccount/legalPage').then(m => ({ default: m.MyAccountLegalPage })));

// Lazy loaded pages - error pages
const MaintenancePage = React.lazy(() => import('@pages/errorPages/maintenancePage').then(m => ({ default: m.MaintenancePage })));
const PageNotFound = React.lazy(() => import('@pages/errorPages/pageNotFound').then(m => ({ default: m.PageNotFound })));

export default function App() {
    const { allLanguages, allSettings, isEndpointExpired } = useData();
    const { request: requestAllLanguages, loading: loadingAllLanguages } = useGetAllLanguages();

    // update PWA manifest with dynamic content from settings
    // like name, logos, colors, etc
    useDynamicManifest();

    const location = useLocation();
    const currentPath = location.pathname;
    const urlSegments = currentPath.split('/');
    const [currentFaviconSrc, setCurrentFaviconSrc] = useState<string>('');

    const isAllLanguagesExpired = isEndpointExpired('allLanguages');
    useEffect(() => {
        (!allLanguages || isAllLanguagesExpired) && !loadingAllLanguages && requestAllLanguages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allLanguages, isAllLanguagesExpired]);

    useEffect(() => {
        if (allSettings?.data) {
            const data = allSettings.data;
            const faviconItem = data.layouts?.find((item) => item.name === 'favicon');

            let finalSrc = null;
            if (faviconItem?.value) {
                finalSrc = faviconItem.value;
            }

            if (finalSrc) {
                setCurrentFaviconSrc(finalSrc);

                const existingLinks = document.querySelectorAll("link[rel*='icon']");
                existingLinks.forEach((link) => link.remove());

                const link = document.createElement('link');
                link.rel = 'icon';
                link.href = finalSrc;
                link.type = 'image/x-icon';

                document.head.appendChild(link);
            }
        }

        return () => {
            if (currentFaviconSrc) {
                const dynamicLink = document.querySelector(`link[href="${currentFaviconSrc}"]`);
                if (dynamicLink) {
                    dynamicLink.remove();
                }
            }
        };
    }, [allSettings, currentFaviconSrc]);

    const supportedLanguages = allLanguages?.data
        ?.filter((item) => item.language_code !== undefined)
        .map((item) => item.language_code);

    // waiting for languages service return
    if (supportedLanguages?.length === 0) {
        return <></>;
    }

    const availableRoutes = () => (
        <>
            {/* authentication routes */}
            <Route path={SITE_URLS.createAccount} element={<CreateAccountPage />} />
            <Route path={SITE_URLS.login} element={<LoginPage />} />
            <Route path={SITE_URLS.recoverPassword} element={<PasswordRecoveryPage />} />
            <Route path={SITE_URLS.setNewPassword} element={<SetNewPasswordPage />} />
            <Route path={SITE_URLS.twoFactorAuthActivate} element={<TwoFactorAuthActivatePage />} />
            <Route path={SITE_URLS.twoFactorAuthVerify} element={<TwoFactorAuthVerifyPage />} />
            {/* marketplace routes */}
            <Route path={SITE_URLS.home} element={<HomePage />} />
            <Route path={SITE_URLS.products} element={<ProductsPage />} />
            <Route path={SITE_URLS.productsSearch} element={<ProductsSearchResultsPage />} />
            <Route path={SITE_URLS.productsSubcategory} element={<ProductsSubcategoryPage />} />
            <Route path={SITE_URLS.productDetail} element={<ProductDetailPage />} />
            <Route path={SITE_URLS.services} element={<ServicesPage />} />
            <Route path={SITE_URLS.servicesSearch} element={<ServicesSearchResultsPage />} />
            <Route path={SITE_URLS.merchants} element={<MerchantsPage />} />
            <Route path={SITE_URLS.merchantDetail} element={<MerchantDetailPage />} />
            <Route path={SITE_URLS.promoBaskets} element={<BasketsPage />} />
            <Route path={SITE_URLS.promoBasketDetail} element={<BasketDetailPage />} />
            <Route path={SITE_URLS.campaigns} element={<CampaignsPage />} />
            <Route path={SITE_URLS.contacts} element={<ContactsPage />} />
            <Route path={SITE_URLS.howToUsePoints} element={<HowToUsePointsPage />} />
            <Route path={SITE_URLS.pointsServices} element={<PointsServicesPage />} />
            <Route path={SITE_URLS.pointsServiceDetail} element={<PointsServiceDetailPage />} />
            <Route path={SITE_URLS.policy} element={<PolicyPage />} />
            {/* shopping cart pages */}
            <Route path={SITE_URLS.shoppingCart} element={<ShoppingCartPage />} />
            <Route path={SITE_URLS.orderBilling} element={<BillingPage />} />
            <Route path={SITE_URLS.orderDeliveryAddress} element={<DeliveryAddressPage />} />
            <Route path={SITE_URLS.orderPayment} element={<PaymentPage />} />
            <Route path={SITE_URLS.orderConfirm} element={<ConfirmOrderPage />} />
            <Route path={SITE_URLS.orderFeedback} element={<FeedbackPage />} />
            <Route path={SITE_URLS.cardPaymentRedirect} element={<CardPaymentRedirectPage />} />
            {/* services cart pages */}
            <Route path={SITE_URLS.servicesCart} element={<ServicesCartPage />} />
            <Route path={SITE_URLS.servicesBookingEmployees} element={<ServicesBookingEmployeesPage />} />
            <Route path={SITE_URLS.servicesBookingScheduling} element={<ServicesBookingSchedulingPage />} />
            <Route path={SITE_URLS.servicesBookingPayment} element={<ServicesBookingPaymentPage />} />
            <Route path={SITE_URLS.servicesBookingConfirm} element={<ServicesBookingConfirmPage />} />
            <Route path={SITE_URLS.servicesBookingFeedback} element={<ServicesBookingFeedbackPage />} />
            {/* my account pages */}
            <Route path={SITE_URLS.myDashboard} element={<DashboardPage />} />
            <Route path={SITE_URLS.complaint} element={<ComplaintPage />} />
            <Route path={SITE_URLS.myProfile} element={<MyProfilePage />} />
            <Route path={SITE_URLS.myOrders} element={<MyOrdersPage />} />
            <Route path={SITE_URLS.myOrderDetail} element={<MyOrderDetailPage />} />
            <Route path={SITE_URLS.myProductReservations} element={<MyProductReservationsPage />} />
            <Route path={SITE_URLS.myProductReservationDetail} element={<MyProductReservationDetailPage />} />
            <Route path={SITE_URLS.myCompanies} element={<MyCompaniesPage />} />
            <Route path={SITE_URLS.myCompaniesCreate} element={<MyCompanyCreatePage />} />
            <Route path={SITE_URLS.myCompaniesEdit} element={<MyCompanyPage />} />
            <Route path={SITE_URLS.myAdherences} element={<MyAdherencesPage />} />
            <Route path={SITE_URLS.myAdherenceDetail} element={<MyAdherenceDetailPage />} />
            <Route path={SITE_URLS.myCoupons} element={<MyCouponsPage />} />
            <Route path={SITE_URLS.myCouponsCreate} element={<MyCouponsCreatePage />} />
            <Route path={SITE_URLS.myCouponsEdit} element={<MyCouponsEditPage />} />
            <Route path={SITE_URLS.myServices} element={<ManageServicesPage />} />
            <Route path={SITE_URLS.myServicesEdit} element={<ManageServicesEditPage />} />
            <Route path={SITE_URLS.myServicesCreate} element={<ManageServicesCreatePage />} />
            <Route path={SITE_URLS.myWorkers} element={<ManageWorkersPage />} />
            <Route path={SITE_URLS.myWorkersEdit} element={<ManageWorkersEditPage />} />
            <Route path={SITE_URLS.myWorkersCreate} element={<ManageWorkersCreatePage />} />
            <Route path={SITE_URLS.mySchedule} element={<ManageSchedulePage />} />
            <Route path={SITE_URLS.myProducts} element={<ManageProductsPage />} />
            <Route path={SITE_URLS.myProductsCreate} element={<ManageProductsCreatePage />} />
            <Route path={SITE_URLS.myProductsEdit} element={<ManageProductsEditPage />} />
            <Route path={SITE_URLS.manageOrders} element={<ManageOrdersPage />} />
            <Route path={SITE_URLS.manageOrderDetail} element={<ManageOrderDetailPage />} />
            <Route path={SITE_URLS.manageProductReservations} element={<ManageProductReservationsPage />} />
            <Route path={SITE_URLS.manageProductReservationDetail} element={<ManageProductReservationDetailPage />} />
            <Route path={SITE_URLS.myPoints} element={<MyAccountPointsHistoryPage />} />
            <Route path={SITE_URLS.myAddresses} element={<ManageAddressesPage />} />
            <Route path={SITE_URLS.myAddressesEdit} element={<ManageAddressesEditPage />} />
            <Route path={SITE_URLS.myAddressesCreate} element={<ManageAddressesCreatePage />} />
            <Route path={SITE_URLS.myHelpCentrePage} element={<HelpCentrePage />} />
            <Route path={SITE_URLS.deleteAccount} element={<DeleteAccountPage />} />
            <Route path={SITE_URLS.deleteAccountCompleted} element={<DeleteAccountCompletedPage />} />
            <Route path={SITE_URLS.myAccountLegal} element={<MyAccountLegalPage />} />
            <Route path={SITE_URLS.myAccountLegalPage} element={<MyAccountLegalPage />} />
            {/* 404 page */}
            <Route path="*" element={<PageNotFound />} />
        </>
    );

    const isInMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true' || false;

    return (
        <Suspense fallback={<div />}>
            <InstallPWAPrompt />
            <Routes>
                {isInMaintenanceMode ? (
                    <Route path="*" element={<MaintenancePage />} />
                ) : (
                    <>
                        {urlSegments.length > 1 && supportedLanguages?.includes(urlSegments[1]) ? (
                            <Route path="/:language" element={<SiteSetup />}>
                                {availableRoutes()}
                            </Route>
                        ) : (
                            <Route path="/" element={<SiteSetup />}>
                                {availableRoutes()}
                            </Route>
                        )}
                    </>
                )}
            </Routes>
        </Suspense>
    );
}

(() => {
    // Preselect language before render: use URL prefix to check if it matches a configured locale(pt,en..), otherwise fall back to stored preference.
    try {
        const firstSeg = window.location.pathname.split('/')[1];
        const configured = (i18n.options?.resources ? Object.keys(i18n.options.resources as Record<string, unknown>) : []) as string[];
        if (firstSeg && configured.includes(firstSeg)) {
            i18n.changeLanguage(firstSeg);
            return;
        }
        const stored = loadFromLocalStorage<string>('language');
        if (stored && configured.includes(stored)) {
            i18n.changeLanguage(stored);
        }
    } catch {
        // ignore
    }
})();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
    <BrowserRouter>
        <React.StrictMode>
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ''}>
                <ThemesProvider>
                    <DataProvider>
                        <NotificationsProvider>
                            <ServicesCartProvider>
                                <App />
                            </ServicesCartProvider>
                        </NotificationsProvider>
                    </DataProvider>
                </ThemesProvider>
            </GoogleOAuthProvider>
        </React.StrictMode>
    </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// register service worker for PWA support
serviceWorkerRegistration.register();
